import { createReducer, on, ActionReducer } from '@ngrx/store';
import { fromCustomImageActions } from './custom-image.actions';
import { CustomImageState, initialCustomImageState } from './custom-image.state';
import { CustomImageImpl } from '@design/models/custom-image.model';

export const customImageReducer: ActionReducer<CustomImageState> = createReducer(
  initialCustomImageState,

  // Upload
  on(fromCustomImageActions.UploadCustomImage, (state) => ({
    ...state,
    isLoading: true,
    uploadError: null
  })),

  on(fromCustomImageActions.UploadCustomImageSuccess, (state, { customImage }) => ({
    ...state,
    customImage,
    isLoading: false,
    uploadError: null,
    isNewImage: true,  // Mark as new image
    preEditSnapshot: null  // No previous state for new images
  })),

  on(fromCustomImageActions.UploadCustomImageFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    uploadError: error
  })),

  // Load
  on(fromCustomImageActions.LoadCustomImageSuccess, (state, { customImage }) => ({
    ...state,
    customImage,
    isLoading: false
  })),

  // Update
  on(fromCustomImageActions.UpdateCustomImage, (state, { customImage }) => ({
    ...state,
    customImage
  })),

  on(fromCustomImageActions.UpdateCustomImagePosition, (state, { x, y, z }) => {
    if (!state.customImage) return state;
    return {
      ...state,
      customImage: new CustomImageImpl({
        ...state.customImage,
        position: { x, y, z }
      })
    };
  }),

  on(fromCustomImageActions.UpdateCustomImageRotation, (state, { rotation }) => {
    if (!state.customImage) return state;
    // Normalize rotation to 0-360
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    return {
      ...state,
      customImage: new CustomImageImpl({
        ...state.customImage,
        rotation: normalizedRotation
      })
    };
  }),

  on(fromCustomImageActions.UpdateCustomImageOpacity, (state, { opacity }) => {
    if (!state.customImage) return state;
    // Clamp opacity between 0 and 100
    const clampedOpacity = Math.max(0, Math.min(100, opacity));
    return {
      ...state,
      customImage: new CustomImageImpl({
        ...state.customImage,
        opacity: clampedOpacity
      })
    };
  }),

  on(fromCustomImageActions.UpdateCustomImageVisibility, (state, { visible }) => {
    if (!state.customImage) return state;
    return {
      ...state,
      customImage: new CustomImageImpl({
        ...state.customImage,
        visible
      })
    };
  }),

  on(fromCustomImageActions.UpdateCustomImageScale, (state, { width, height }) => {
    if (!state.customImage) return state;
    return {
      ...state,
      customImage: new CustomImageImpl({
        ...state.customImage,
        scale: {
          width,
          height,
          aspectRatio: width / height
        }
      })
    };
  }),

  // Calibration
  on(fromCustomImageActions.BeginCalibration, (state, { customImage }) => ({
    ...state,
    isCalibrating: true,
    calibrationPending: true,
    // Only reset points count if calibration points aren't already placed
    // This preserves the count when dialog is shown after placing both points
    calibrationPointsCount: (customImage.calibration?.point1 && customImage.calibration?.point2) 
      ? 2 
      : state.calibrationPointsCount || 0
  })),

  on(fromCustomImageActions.SetCalibrationPointsCount, (state, { count }) => ({
    ...state,
    calibrationPointsCount: count
  })),

  on(fromCustomImageActions.CalibrateCustomImageSuccess, (state, { customImage }) => ({
    ...state,
    customImage,
    // Exit calibration mode after successful calibration
    isCalibrating: false,
    calibrationPending: false,
    calibrationPointsCount: 0
  })),

  on(fromCustomImageActions.CancelCalibration, (state) => ({
    ...state,
    isCalibrating: false,
    calibrationPending: false,
    calibrationPointsCount: 0
  })),

  // Delete
  on(fromCustomImageActions.DeleteCustomImageSuccess, (state) => ({
    ...state,
    customImage: null,
    isMerged: false,  // Reset merged state when custom image is deleted
    isEditMode: false,  // Also reset edit mode
    isNewImage: false,  // Reset new image flag
    preEditSnapshot: null  // Clear any snapshot
  })),

  // Dialog
  on(fromCustomImageActions.ShowConfirmationDialog, (state) => ({
    ...state,
    showConfirmationDialog: true
  })),

  on(fromCustomImageActions.HideConfirmationDialog, (state) => ({
    ...state,
    showConfirmationDialog: false
  })),

  on(fromCustomImageActions.SetDontShowConfirmationAgain, (state, { dontShow }) => ({
    ...state,
    dontShowConfirmationAgain: dontShow
  })),

  // Edit mode
  on(fromCustomImageActions.EnterEditMode, (state) => ({
    ...state,
    isEditMode: true
  })),

  on(fromCustomImageActions.ExitEditMode, (state) => ({
    ...state,
    isEditMode: false
  })),

  // Merge custom image with ground
  on(fromCustomImageActions.MergeCustomImageWithGround, (state) => ({
    ...state,
    isLoading: true
  })),

  on(fromCustomImageActions.MergeCustomImageWithGroundSuccess, (state) => ({
    ...state,
    isLoading: false,
    isMerged: true,
    isEditMode: false,
    isNewImage: false,  // After merge, it's no longer a new image
    preEditSnapshot: null  // Clear the snapshot after successful merge
  })),

  on(fromCustomImageActions.MergeCustomImageWithGroundFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    uploadError: error
  })),

  // Unmerge custom image from ground - restore for editing
  on(fromCustomImageActions.UnmergeCustomImageFromGround, (state) => ({
    ...state,
    isLoading: true
  })),

  on(fromCustomImageActions.UnmergeCustomImageFromGroundSuccess, (state, { preEditSnapshot }) => ({
    ...state,
    isLoading: false,
    isMerged: false,
    isEditMode: true,
    isNewImage: false,  // Explicitly set to false - this is a pre-existing image being edited
    preEditSnapshot: preEditSnapshot ?? state.customImage  // Use passed snapshot or fallback to current
  })),

  on(fromCustomImageActions.UnmergeCustomImageFromGroundFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    uploadError: error
  })),

  // S3 Save
  on(fromCustomImageActions.SaveCustomImageToS3, (state) => ({
    ...state,
    isLoading: true
  })),

  on(fromCustomImageActions.SaveCustomImageToS3Success, (state, { s3Url }) => {
    if (!state.customImage) return { ...state, isLoading: false };
    return {
      ...state,
      customImage: new CustomImageImpl({
        ...state.customImage,
        imageUrl: s3Url
      }),
      isLoading: false
    };
  }),

  on(fromCustomImageActions.SaveCustomImageToS3Failure, (state, { error }) => ({
    ...state,
    isLoading: false,
    uploadError: error
  })),

  // Reset
  on(fromCustomImageActions.ResetCustomImageState, () => initialCustomImageState),

  // Clear error
  on(fromCustomImageActions.ClearUploadError, (state) => ({
    ...state,
    uploadError: null
  })),

  // Store snapshot of custom image before entering edit mode
  on(fromCustomImageActions.StorePreEditSnapshot, (state, { snapshot }) => ({
    ...state,
    preEditSnapshot: snapshot
  })),

  // Cancel edit mode - restore to pre-edit state or delete if new image
  on(fromCustomImageActions.CancelEditMode, (state) => {
    // If it's a new image (not yet merged), delete it
    if (state.isNewImage) {
      return {
        ...state,
        customImage: null,
        isEditMode: false,
        isNewImage: false,
        preEditSnapshot: null
      };
    }
    
    // If it's a pre-merged image, restore it to the snapshot state
    // Don't set isMerged: true here - the effect will trigger the actual merge
    if (state.preEditSnapshot) {
      return {
        ...state,
        customImage: state.preEditSnapshot,
        isEditMode: false,
        // isMerged will be set to true by MergeCustomImageWithGroundSuccess
        preEditSnapshot: null
      };
    }
    
    // Fallback: just exit edit mode
    return {
      ...state,
      isEditMode: false
    };
  })
);
